import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, KeyRound } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Missing reset token");
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 grain">
      <div className="w-full max-w-md relative z-10 fade-up">
        <Link to="/login" data-testid="back-login-link" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
        <div className="border border-white/10 bg-[#121212] p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-1">
            <KeyRound size={18} className="text-[#FF3B30]" />
            <span className="text-xs font-mono uppercase tracking-widest text-zinc-400">Reset Access</span>
          </div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tighter mb-8">New Password</h1>
          {!token ? (
            <p data-testid="reset-invalid" className="text-sm text-zinc-400">This reset link is invalid. Please request a new one from the sign-in page.</p>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">New Password</Label>
                <Input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#FF3B30]" placeholder="••••••••" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Confirm Password</Label>
                <Input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                  className="bg-transparent border-white/15 rounded-none h-12 focus-visible:ring-[#FF3B30]" placeholder="••••••••" />
              </div>
              <button data-testid="reset-submit-btn" type="submit" disabled={busy}
                className="w-full bg-white text-black font-bold py-3.5 uppercase tracking-wider text-sm hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-50">
                {busy ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
